import { http } from './api.service'
import store from '../store'

const application = process.env.VUE_APP_APPLICATION

/**
 * Thin wrapper around the Laravel AI agent endpoints (/v2/ai/*).
 *
 * The shared http instance already injects the `Domain` header (the currently
 * selected domain) which the API uses for role/team context. The `domainUuid`
 * arguments below are separate: they target which tenant a document belongs to
 * or a question is scoped against, and are sent in the body/query explicitly.
 */
const aiService = {
  // --- Quick agent ------------------------------------------------------
  ask ({ question, domainUuid = null, conversationId = null }) {
    return http.post('/ai/chat', {
      question,
      domain_uuid: domainUuid || null,
      conversation_id: conversationId || undefined
    }).then(({ data }) => data)
  },

  async stream ({ question, domainUuid = null, conversationId = null, onEvent, signal }) {
    const baseUrl = String(process.env.VUE_APP_API_URL || '').replace(/\/$/, '')
    const response = await fetch(`${baseUrl}/ai/chat/stream`, {
      method: 'POST',
      headers: {
        Accept: 'text/event-stream',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${store.getters.jwt}`,
        Application: application,
        Domain: store.getters.domain.uuid,
        'X-DOMAIN': store.getters.domain.uuid,
        'X-APPLICATION': application,
        'X-SOURCE': 'administration'
      },
      body: JSON.stringify({
        question,
        domain_uuid: domainUuid || null,
        conversation_id: conversationId || undefined
      }),
      signal
    })

    if (!response.ok) {
      let data = {}
      try {
        data = await response.json()
      } catch (_) {}
      const error = new Error(data.message || `HTTP ${response.status}`)
      error.response = { status: response.status, data }
      throw error
    }

    if (!response.body) {
      parseSseBuffer(await response.text(), onEvent, true)
      return
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    while (true) {
      const { done, value } = await reader.read()
      buffer += decoder.decode(value || new Uint8Array(), { stream: !done })
      buffer = parseSseBuffer(buffer, onEvent, done)
      if (done) {
        break
      }
    }
  },

  // --- Knowledge base ---------------------------------------------------
  listDocuments (domainUuid = null) {
    const params = domainUuid ? { domain_uuid: domainUuid } : {}
    return http.get('/ai/knowledge/documents', { params }).then(({ data }) => data)
  },

  ingestText (payload) {
    return http.post('/ai/knowledge/text', payload).then(({ data }) => data)
  },

  ingestFile (formData) {
    return http.post('/ai/knowledge/file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(({ data }) => data)
  },

  ingestPdf (formData) {
    return this.ingestFile(formData)
  },

  deleteDocument (id, expectedRevision) {
    return http.delete(`/ai/knowledge/documents/${id}`, {
      data: { expected_revision: expectedRevision }
    }).then(({ data }) => data)
  },

  updateDocument (id, payload, expectedRevision) {
    return http.put(`/ai/knowledge/documents/${id}`, {
      ...payload,
      expected_revision: expectedRevision
    }).then(({ data }) => data)
  },

  getDocumentText (id) {
    return http.get(`/ai/knowledge/documents/${id}/text`).then(({ data }) => data)
  },

  updateDocumentText (id, text, expectedRevision, pageOffsets = []) {
    return http.put(`/ai/knowledge/documents/${id}/text`, {
      text,
      expected_revision: expectedRevision,
      page_offsets: pageOffsets
    }).then(({ data }) => data)
  },

  reindexDocument (id, expectedRevision) {
    return http.post(`/ai/knowledge/documents/${id}/reindex`, {
      expected_revision: expectedRevision
    }).then(({ data }) => data)
  },

  reextractDocument (id, expectedRevision) {
    return http.post(`/ai/knowledge/documents/${id}/reextract`, {
      replace_canonical: true,
      expected_revision: expectedRevision
    }).then(({ data }) => data)
  },

  downloadOriginalDocument (id) {
    return http.get(`/ai/knowledge/documents/${id}/original`, { responseType: 'blob' })
  },

  // --- Quotas (super-admin) --------------------------------------------
  listQuotas () {
    return http.get('/ai/quotas').then(({ data }) => data)
  },

  setDomainQuota (domainUuid, { domainMonthlyLimit = null, monthlyUserLimit }) {
    return http.put(`/ai/quotas/${domainUuid}`, {
      domain_monthly_limit: domainMonthlyLimit,
      monthly_user_limit: monthlyUserLimit
    }).then(({ data }) => data)
  },

  clearDomainQuota (domainUuid) {
    return http.delete(`/ai/quotas/${domainUuid}`).then(({ data }) => data)
  },

  // --- Tracking --------------------------------------------------------
  listTracking (domainUuid = null) {
    const params = domainUuid ? { domain_uuid: domainUuid } : {}
    return http.get('/ai/tracking', { params }).then(({ data }) => data)
  },

  exportTrackingCsv (domainUuid = null) {
    const params = domainUuid ? { domain_uuid: domainUuid } : {}
    return http.get('/ai/tracking/export', {
      params,
      responseType: 'blob'
    })
  },

  // --- Domain insights (directors / operators) ---------------------------
  // Scoped by the API to the domain sent in the `Domain` header, so unlike the
  // internal tracking above there is no domain_uuid argument.
  domainStats ({ dateStart = null, dateEnd = null } = {}) {
    const params = {}
    if (dateStart) params.dateStart = dateStart
    if (dateEnd) params.dateEnd = dateEnd
    return http.get('/ai/domain/stats', { params }).then(({ data }) => data)
  },

  domainRequests ({ dateStart = null, dateEnd = null } = {}) {
    const params = {}
    if (dateStart) params.dateStart = dateStart
    if (dateEnd) params.dateEnd = dateEnd
    return http.get('/ai/domain/requests', { params }).then(({ data }) => data)
  },

  listDomainDocuments () {
    return http.get('/ai/domain/knowledge/documents').then(({ data }) => data)
  },

  // --- Réunions CSE ------------------------------------------------------
  // Scoped by the API to the domain of the `Domain` header, like the insights
  // above. The list omits the long texts; `getMeeting` returns everything.
  listMeetings () {
    return http.get('/ai/meetings').then(({ data }) => data)
  },

  // `formData` carries input_type + upload_id (audio sent in chunks, see
  // below) or notes_text (text), plus the optional title and meeting_date.
  // Resolves with the queued meeting (202).
  createMeeting (formData, { onUploadProgress } = {}) {
    return http.post('/ai/meetings', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress
    }).then(({ data }) => data)
  },

  // Recordings go through Cloudflare, which refuses any request above 100 MB:
  // the file is described first, then sent chunk by chunk, and `createMeeting`
  // receives the upload_id. Resolves with { upload_id, chunk_size, chunk_count }.
  startMeetingUpload ({ name, size, mimeType }) {
    return http.post('/ai/meetings/uploads', { name, size, mime_type: mimeType || null }).then(({ data }) => data)
  },

  // One chunk (a Blob of exactly chunk_size bytes, the last one shorter).
  // Resolves with { received, chunk_count }.
  uploadMeetingChunk (uploadId, index, blob, { onUploadProgress } = {}) {
    const formData = new FormData()
    formData.append('index', index)
    formData.append('file', blob, `chunk-${index}`)
    return http.post(`/ai/meetings/uploads/${uploadId}/chunks`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress
    }).then(({ data }) => data)
  },

  getMeeting (uuid) {
    return http.get(`/ai/meetings/${uuid}`).then(({ data }) => data)
  },

  retryMeeting (uuid) {
    return http.post(`/ai/meetings/${uuid}/retry`).then(({ data }) => data)
  },

  // Decisions of an elected member on the corrections proposed on the
  // transcript: [{ id, decision: 'accept' | 'reject' | 'edit', to, glossary }].
  // An empty list means "write the deliverables without reviewing". Resolves
  // with the queued meeting (202) plus a `review_summary`.
  reviewMeeting (uuid, decisions) {
    return http.post(`/ai/meetings/${uuid}/review`, { decisions }).then(({ data }) => data)
  },

  // Names given by the reviewer to the voices of the transcript, keyed by the
  // provider's raw speaker label and sent whole each time (empty = unnamed).
  // Resolves with the updated meeting.
  nameMeetingSpeakers (uuid, names) {
    return http.put(`/ai/meetings/${uuid}/speakers`, { names }).then(({ data }) => data)
  },

  // Reassigns one phrase of the transcript (segments `from` to `to`, inclusive
  // positions) to another voice: a raw speaker label already present in the
  // transcript, or 'new' for a voice that does not exist yet. Refused (409)
  // while the meeting is being processed. Resolves with the updated meeting.
  moveMeetingSegments (uuid, { from, to, speaker }) {
    return http.put(`/ai/meetings/${uuid}/segments/speaker`, { from, to, speaker }).then(({ data }) => data)
  },

  deleteMeeting (uuid) {
    return http.delete(`/ai/meetings/${uuid}`)
  },

  // --- Dossier de suivi du CSE --------------------------------------------
  // The running memory of the domain, rewritten by each meeting's synthesis
  // and editable by hand. `getMeetingContext` resolves with { current, versions }
  // (the current version carries its text, the list does not).
  getMeetingContext () {
    return http.get('/ai/meetings/context').then(({ data }) => data)
  },

  getMeetingContextVersion (id) {
    return http.get(`/ai/meetings/context/versions/${id}`).then(({ data }) => data)
  },

  // Saves a hand-corrected text as a new version; resolves like getMeetingContext.
  updateMeetingContext (text) {
    return http.put('/ai/meetings/context', { text }).then(({ data }) => data)
  }
}

function parseSseBuffer (buffer, onEvent, flush) {
  const normalized = buffer.replace(/\r\n/g, '\n')
  const blocks = normalized.split('\n\n')
  const remainder = flush ? '' : (blocks.pop() || '')

  blocks.forEach(block => emitSseBlock(block, onEvent))
  if (flush && blocks.length === 0 && normalized.trim()) {
    emitSseBlock(normalized, onEvent)
  }

  return remainder
}

function emitSseBlock (block, onEvent) {
  if (!block.trim()) {
    return
  }

  const lines = block.split('\n')
  const eventLine = lines.find(line => line.startsWith('event:'))
  const data = lines
    .filter(line => line.startsWith('data:'))
    .map(line => line.slice(5).replace(/^\s+/, ''))
    .join('\n')

  if (!eventLine || !data) {
    return
  }

  try {
    onEvent({
      event: eventLine.slice(6).trim(),
      data: JSON.parse(data)
    })
  } catch (_) {}
}

export default aiService
export { aiService }
